/**
 * Progressive-enhancement dropdown for the small-world dialog. The native
 * `<select>` stays in the DOM as the source of truth for its value, form
 * semantics and `main.ts` reads (`readCreateRoomConfig` /
 * `localizeRoomConfigControls`); this only layers a 国潮-styled combobox +
 * listbox on top and mirrors the selection back onto the select.
 *
 * ARIA: a `button[aria-haspopup=listbox]` trigger and a `ul[role=listbox]` with
 * `role=option` children. Keyboard: Up/Down/Home/End move the active option,
 * Enter/Space commit, Esc closes, printable keys type-ahead.
 */

interface RoomSelectWidget {
  readonly select: HTMLSelectElement;
  readonly trigger: HTMLButtonElement;
  readonly list: HTMLUListElement;
  activeIndex: number;
  open: boolean;
  typeAhead: string;
  typeAheadClearId: number | null;
}

const widgets: RoomSelectWidget[] = [];
let outsideHandlerBound = false;

/** Enhance every `.room-frame-field select` (idempotent). */
export function enhanceRoomSelects(root: ParentNode = document): void {
  const selects = root.querySelectorAll<HTMLSelectElement>(
    ".room-frame-field select",
  );
  for (const select of selects) {
    if (select.dataset.enhanced === "true") {
      continue;
    }
    enhanceOne(select);
  }
  if (!outsideHandlerBound) {
    document.addEventListener("pointerdown", (event) => {
      for (const widget of widgets) {
        if (
          widget.open &&
          !widget.trigger.contains(event.target as Node) &&
          !widget.list.contains(event.target as Node)
        ) {
          closeWidget(widget, false);
        }
      }
    });
    outsideHandlerBound = true;
  }
}

let uid = 0;

function enhanceOne(select: HTMLSelectElement): void {
  select.dataset.enhanced = "true";
  const id = `room-select-${((uid += 1)).toString()}`;

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "room-select-trigger";
  trigger.id = `${id}-trigger`;
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  const label = document.createElement("span");
  label.className = "room-select-value";
  const chevron = document.createElement("span");
  chevron.className = "room-select-chevron";
  chevron.setAttribute("aria-hidden", "true");
  trigger.append(label, chevron);

  const list = document.createElement("ul");
  list.className = "room-select-list is-hidden";
  list.id = `${id}-list`;
  list.setAttribute("role", "listbox");
  list.tabIndex = -1;
  trigger.setAttribute("aria-controls", list.id);

  // Insert the trigger + list right after the (now visually hidden) select.
  select.classList.add("room-select-native");
  select.after(trigger, list);

  const widget: RoomSelectWidget = {
    select,
    trigger,
    list,
    activeIndex: select.selectedIndex,
    open: false,
    typeAhead: "",
    typeAheadClearId: null,
  };
  widgets.push(widget);

  rebuildOptions(widget);
  syncTriggerLabel(widget);

  trigger.addEventListener("click", () => toggleWidget(widget));
  trigger.addEventListener("keydown", (event) =>
    onTriggerKeydown(widget, event),
  );
  list.addEventListener("keydown", (event) => onListKeydown(widget, event));

  // Keep the widget in sync when option text (locale switch) or value changes
  // outside the widget.
  select.addEventListener("change", () => {
    widget.activeIndex = select.selectedIndex;
    syncTriggerLabel(widget);
    syncActiveOption(widget);
  });
  const observer = new MutationObserver(() => {
    rebuildOptions(widget);
    syncTriggerLabel(widget);
  });
  observer.observe(select, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

function rebuildOptions(widget: RoomSelectWidget): void {
  const { list, select } = widget;
  list.replaceChildren();
  Array.from(select.options).forEach((option, index) => {
    const item = document.createElement("li");
    item.className = "room-select-option";
    item.id = `${list.id}-opt-${index.toString()}`;
    item.setAttribute("role", "option");
    item.setAttribute("aria-selected", option.selected ? "true" : "false");
    item.dataset.index = index.toString();
    item.textContent = option.textContent;
    item.addEventListener("click", () => commit(widget, index));
    item.addEventListener("pointermove", () => setActive(widget, index));
    list.append(item);
  });
}

function syncTriggerLabel(widget: RoomSelectWidget): void {
  const option = widget.select.options[widget.select.selectedIndex];
  const value = widget.trigger.querySelector(".room-select-value");
  if (value) {
    value.textContent = option?.textContent ?? "";
  }
}

function syncActiveOption(widget: RoomSelectWidget): void {
  const items = widget.list.querySelectorAll<HTMLLIElement>(
    ".room-select-option",
  );
  items.forEach((item, index) => {
    const selected = index === widget.select.selectedIndex;
    item.setAttribute("aria-selected", selected ? "true" : "false");
    item.classList.toggle("is-active", index === widget.activeIndex);
  });
  const active = items[widget.activeIndex];
  if (active) {
    widget.list.setAttribute("aria-activedescendant", active.id);
    active.scrollIntoView({ block: "nearest" });
  }
}

function setActive(widget: RoomSelectWidget, index: number): void {
  const max = widget.select.options.length - 1;
  widget.activeIndex = Math.max(0, Math.min(max, index));
  syncActiveOption(widget);
}

function toggleWidget(widget: RoomSelectWidget): void {
  if (widget.open) {
    closeWidget(widget, true);
  } else {
    openWidget(widget);
  }
}

function openWidget(widget: RoomSelectWidget): void {
  // Close any other open widget first.
  for (const other of widgets) {
    if (other !== widget && other.open) {
      closeWidget(other, false);
    }
  }
  widget.open = true;
  widget.activeIndex =
    widget.select.selectedIndex >= 0 ? widget.select.selectedIndex : 0;
  widget.list.classList.remove("is-hidden");
  widget.trigger.setAttribute("aria-expanded", "true");
  syncActiveOption(widget);
  widget.list.focus();
}

function closeWidget(widget: RoomSelectWidget, focusTrigger: boolean): void {
  if (!widget.open) {
    return;
  }
  widget.open = false;
  widget.list.classList.add("is-hidden");
  widget.trigger.setAttribute("aria-expanded", "false");
  if (focusTrigger) {
    widget.trigger.focus();
  }
}

function commit(widget: RoomSelectWidget, index: number): void {
  const option = widget.select.options[index];
  if (option && widget.select.selectedIndex !== index) {
    widget.select.selectedIndex = index;
    widget.select.dispatchEvent(new Event("change", { bubbles: true }));
  }
  widget.activeIndex = index;
  syncTriggerLabel(widget);
  syncActiveOption(widget);
  closeWidget(widget, true);
}

function onTriggerKeydown(
  widget: RoomSelectWidget,
  event: KeyboardEvent,
): void {
  if (
    event.key === "ArrowDown" ||
    event.key === "ArrowUp" ||
    event.key === "Enter" ||
    event.key === " "
  ) {
    event.preventDefault();
    openWidget(widget);
  }
}

function onListKeydown(widget: RoomSelectWidget, event: KeyboardEvent): void {
  const lastIndex = widget.select.options.length - 1;
  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      setActive(widget, widget.activeIndex + 1);
      break;
    case "ArrowUp":
      event.preventDefault();
      setActive(widget, widget.activeIndex - 1);
      break;
    case "Home":
      event.preventDefault();
      setActive(widget, 0);
      break;
    case "End":
      event.preventDefault();
      setActive(widget, lastIndex);
      break;
    case "Enter":
    case " ":
      event.preventDefault();
      commit(widget, widget.activeIndex);
      break;
    case "Escape":
      event.preventDefault();
      closeWidget(widget, true);
      break;
    case "Tab":
      closeWidget(widget, false);
      break;
    default:
      if (event.key.length === 1) {
        typeAhead(widget, event.key);
      }
  }
}

function typeAhead(widget: RoomSelectWidget, char: string): void {
  widget.typeAhead += char.toLowerCase();
  if (widget.typeAheadClearId !== null) {
    window.clearTimeout(widget.typeAheadClearId);
  }
  widget.typeAheadClearId = window.setTimeout(() => {
    widget.typeAhead = "";
    widget.typeAheadClearId = null;
  }, 600);
  const match = Array.from(widget.select.options).findIndex((option) =>
    (option.textContent ?? "").toLowerCase().startsWith(widget.typeAhead),
  );
  if (match >= 0) {
    setActive(widget, match);
  }
}
